import { useMutation, useQuery } from '@apollo/client'
import { StickyNote2, StickyNote2Outlined } from '@mui/icons-material'
import {
    Button,
    IconButton,
    Popover,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
    type RosterProfileQuery,
    type RosterProfileQueryVariables,
} from '__generated__/graphql'
import { useEffect, useRef, useState } from 'react'
import { EditRoster } from 'recruiter/components/RosterDrawer/AddOrEdit.gql'
import { GetRosterProfile } from 'recruiter/graphql/rosters.gql'
import { getPxAsRem } from 'shared/utils'
import { useRosterSearch } from './columns/RosterSearchContext'

export function ToolbarScratchPad() {
    const { rosterId } = useRosterSearch()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [draft, setDraft] = useState('')
    const textFieldRef = useRef<HTMLTextAreaElement>(null)

    const { data } = useQuery<RosterProfileQuery, RosterProfileQueryVariables>(
        GetRosterProfile as TypedDocumentNode<
            RosterProfileQuery,
            RosterProfileQueryVariables
        >,
        { variables: { id: rosterId } }
    )

    const scratchPad = data?.roster?.scratchPad ?? ''

    const [updateRoster, { loading: saving }] = useMutation(EditRoster, {
        refetchQueries: [
            { query: GetRosterProfile, variables: { id: rosterId } },
        ],
    })

    const open = Boolean(anchorEl)
    const hasContent = scratchPad.length > 0

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setDraft(scratchPad)
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    useEffect(() => {
        if (open && textFieldRef.current) {
            textFieldRef.current.focus()
        }
    }, [open])

    const handleSave = async () => {
        if (draft === scratchPad) {
            handleClose()
            return
        }
        await updateRoster({ variables: { rosterId, scratchPad: draft } })
        handleClose()
    }

    return (
        <>
            <Tooltip title="Scratch Pad" arrow>
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{ padding: getPxAsRem(2) }}
                >
                    {hasContent ? (
                        <StickyNote2
                            sx={{
                                fontSize: getPxAsRem(16),
                                color: 'text.secondary',
                            }}
                        />
                    ) : (
                        <StickyNote2Outlined
                            sx={{
                                fontSize: getPxAsRem(16),
                                color: 'text.disabled',
                            }}
                        />
                    )}
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 360, padding: 2 } } }}
            >
                <Stack gap={1.5}>
                    <TextField
                        inputRef={textFieldRef}
                        multiline
                        minRows={3}
                        maxRows={10}
                        fullWidth
                        placeholder="Notes about this search..."
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        size="small"
                    />
                    <Stack direction="row" justifyContent="flex-end" gap={1}>
                        <Button size="small" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || draft === scratchPad}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </>
    )
}
